//-- Copyright (c) 2021 TMT Insights Group, LLC
import { LoadingStatuses } from '@/constants/constants';
import { createSelector } from '@reduxjs/toolkit';
import { type RootState } from '@/redux/store';

export const selectRootJob = (state: RootState) => state?.job || {};
export const selectJob = (state: RootState) => state?.job?.data || {};
export const selectJobMetadata = createSelector(selectJob, (job) => job.context?.metadata);
export const selectJobIsLoading = (state: RootState) => state.job?.status === LoadingStatuses.pending && !state.job?.data?.id;
export const selectJobAssets = createSelector(selectJob, (job) => job.assets || {});
export const selectJobRelatedFiles = createSelector(selectRootJob, (job) => job?.relatedFiles?.data || []);
export const selectJobRelatedFilesPagination = createSelector(selectRootJob, (job) => job.pagination || {});
export const selectJobRelatedFilesIsLoading = createSelector(selectRootJob, (job) => job?.relatedFiles?.status === LoadingStatuses.pending);
export const selectJobRelatedFilesCount = createSelector(selectRootJob, (job) => job?.relatedFiles?.data?.length || 0);
export const selectJobCreatedAt = createSelector(selectJob, (job) => job.created_at);
export const selectJobHid = createSelector(selectJob, (job) => job.hid);

export const selectJobDeliverables = createSelector(
  [selectJob, selectJobAssets], // Input selectors
  (job, jobAssets) => {
    const deliverables = job.deliverables || [];
    const assetDeliverables = jobAssets.deliverables || [];
    return deliverables.length ? deliverables : assetDeliverables;
  }
);
export const selectJobTimeline = createSelector(selectJob, (job) => job.task_timeline || []);

export const selectOriginJobId = createSelector(selectJob, (job) => job.origin_job?.id);

export const selectAudioTracks = createSelector(
  [selectJobMetadata], // Input selector
  (jobMetadata) => {
    let audioTracks = jobMetadata?.['audio-track-n'] || [];
    if (!Array.isArray(audioTracks)) return [];
    audioTracks = audioTracks.filter((track) => track.lang?.length);
    audioTracks.sort((a, b) => Number(a.track) - Number(b.track));
    return audioTracks;
  }
);

export const selectSubtitleTracks = createSelector(
  [selectJobMetadata], // Input selector
  (jobMetadata) => {
    let subtitleTracks = jobMetadata?.['subtitle-n'] || [];
    if (!Array.isArray(subtitleTracks)) return [];
    subtitleTracks = subtitleTracks.filter((track) => track.lang?.length);
    subtitleTracks.sort((a, b) => Number(a.track) - Number(b.track));
    return subtitleTracks;
  }
);
